/**
 * Edit chat — pure helpers for the free-form chat that edits an SVGHMI in
 * precise steps (POST /ai-generator/edit-chat, SSE /edit-chat/:turnId/stream).
 *
 * Everything here is dependency-free so it runs under plain `node`
 * (see __tests__/editChatState.test.js): the SSE line parser, the message state
 * machine (reducer), the poll-snapshot → events mapping, the cost label, the
 * error wording and the suggestion chips.
 *
 * Message shape (state.messages[]):
 *   { id, role: 'user'|'assistant', text, status: 'pending'|'done'|'error',
 *     steps: string[], jobIdBefore, jobIdAfter, diff: string[],
 *     creditsCharged, changed, createdAt }
 */

export const CHAT_MAX_LENGTH = 1000;
const MAX_CHIPS = 4;

export const initialChatState = Object.freeze({
  messages: [],
  // 'idle' | 'sending' (POST in flight) | 'running' (turn streaming)
  status: 'idle',
  turn: null, // { turnId, jobId, startedAt, estimate, pendingId }
  error: null, // { code, message, buy } — a failed send (no turn started)
  historyLoaded: false,
  allowance: null, // { editsLeft, nextCost, included } — see allowanceFrom
});

let idSeq = 0;
/** Local id for a message the server has not numbered. */
export const nextMessageId = (prefix = 'm') => {
  idSeq += 1;
  return `${prefix}${Date.now().toString(36)}${idSeq}`;
};

const asText = (v) => (typeof v === 'string' ? v : '');
const stepText = (s) => {
  if (typeof s === 'string') return s;
  if (s && typeof s.text === 'string') return s.text;
  return '';
};
const asDiff = (v) => (Array.isArray(v) ? v.map((x) => String(x)).filter(Boolean) : []);

const patchPending = (state, patch) => {
  const pendingId = state.turn?.pendingId;
  if (!pendingId) return state;
  return {
    ...state,
    messages: state.messages.map((m) => {
      if (m.id !== pendingId) return m;
      return typeof patch === 'function' ? patch(m) : { ...m, ...patch };
    }),
  };
};

/**
 * GET /edit-chat/history → messages[] in our shape.
 * Server rows: { role, text, job_id_after, created_at }.
 */
export function historyToMessages(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r) => r && (r.role === 'user' || r.role === 'assistant'))
    .map((r, i) => ({
      id: `h${i}-${r.created_at || ''}`,
      role: r.role,
      text: asText(r.text),
      status: 'done',
      steps: [],
      jobIdBefore: null,
      jobIdAfter: r.job_id_after || null,
      diff: asDiff(r.diff_summary),
      creditsCharged: typeof r.credits_charged === 'number' ? r.credits_charged : null,
      changed: !!r.job_id_after,
      createdAt: r.created_at || null,
    }));
}

/**
 * Edit-pack allowance from any server payload that carries it (POST
 * /edit-chat, the `done` event, GET /edit-chat/history). Null fields =
 * not reported. Pricing (owner, 2026-10-13): a generated widget includes its
 * first 10 changes; an imported file's first change costs 1 credit and opens
 * a pack of 10; a used-up pack → the next change costs 1 credit and opens 10
 * more. Questions and failed/reverted turns are free.
 *
 * @returns {{ editsLeft: number|null, nextCost: number|null, included: number|null }|null}
 */
export function allowanceFrom(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const num = (v) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null);
  const out = {
    editsLeft: num(obj.edits_left_in_pack),
    nextCost: num(obj.next_change_costs),
    included: num(obj.included_edits),
  };
  if (out.editsLeft === null && out.nextCost === null && out.included === null) return null;
  return out;
}

const mergeAllowance = (prev, obj) => {
  const next = allowanceFrom(obj);
  if (!next) return prev;
  if (!prev) return next;
  return {
    editsLeft: next.editsLeft ?? prev.editsLeft,
    nextCost: next.nextCost ?? prev.nextCost,
    included: next.included ?? prev.included,
  };
};

const credits = (n) => `${n} ${n === 1 ? 'credit' : 'credits'}`;

/**
 * The backend's poll answer carries the turn as the same event list the
 * stream sends (`events: [{event, data}]`); fold it into the flat fields the
 * reducer reads. Flat fields already present win.
 */
export function snapshotFromEvents(snap) {
  if (!snap || !Array.isArray(snap.events)) return snap || {};
  const out = { ...snap };
  const steps = [];
  const messages = [];
  snap.events.forEach(({ event, data } = {}) => {
    if (event === 'step') steps.push(data);
    else if (event === 'message') messages.push(data);
    else if (event === 'revision' && !out.revision) out.revision = data;
    else if (event === 'done' && !out.done) out.done = data || {};
    else if (event === 'error' && !out.error) out.error = data || {};
  });
  if (!out.steps && steps.length) out.steps = steps;
  if (!out.messages && !out.message && messages.length) out.messages = messages;
  return out;
}

/**
 * The chat state machine. Actions:
 *   reset · history {rows, meta} · send {text, jobId, now, userId?, pendingId?}
 *   started {turnId, estimate, meta} · sendFailed {error}
 *   step {text} · message {text} · revision {job_id, diff_summary}
 *   done {credits_charged, changed, edits_left_in_pack, next_change_costs}
 *   error {code, message}
 *   snapshot {snapshot} (poll fallback, replaces the pending message)
 *   clearError
 */
export function chatReducer(state, action) {
  const s = state || initialChatState;
  switch (action?.type) {
  case 'reset':
    return { ...initialChatState };

  case 'history': {
    // Never clobber a conversation the user is already having.
    const allowance = mergeAllowance(s.allowance, action.meta);
    if (s.messages.length > 0 || s.status !== 'idle') return { ...s, allowance, historyLoaded: true };
    return {
      ...s, allowance, messages: historyToMessages(action.rows), historyLoaded: true,
    };
  }

  case 'send': {
    if (s.status !== 'idle') return s;
    const text = asText(action.text).trim();
    if (!text) return s;
    const now = action.now || Date.now();
    const userMsg = {
      id: action.userId || nextMessageId('u'),
      role: 'user',
      text,
      status: 'done',
      steps: [],
      jobIdBefore: action.jobId || null,
      jobIdAfter: null,
      diff: [],
      creditsCharged: null,
      changed: false,
      createdAt: now,
    };
    const pending = {
      id: action.pendingId || nextMessageId('a'),
      role: 'assistant',
      text: '',
      status: 'pending',
      steps: [],
      jobIdBefore: action.jobId || null,
      jobIdAfter: null,
      diff: [],
      creditsCharged: null,
      changed: false,
      createdAt: now,
    };
    return {
      ...s,
      status: 'sending',
      error: null,
      messages: [...s.messages, userMsg, pending],
      turn: {
        turnId: null,
        jobId: action.jobId || null,
        startedAt: now,
        estimate: null,
        pendingId: pending.id,
      },
    };
  }

  case 'started':
    if (!s.turn) return s;
    return {
      ...s,
      status: 'running',
      allowance: mergeAllowance(s.allowance, action.meta),
      turn: { ...s.turn, turnId: action.turnId || null, estimate: action.estimate || null },
    };

  case 'sendFailed': {
    // No turn was created: drop the pending bubble, keep the user's text
    // in the list only if the caller wants it back in the input (it does).
    const pendingId = s.turn?.pendingId;
    const messages = s.messages.slice();
    if (pendingId && messages.length >= 2 && messages[messages.length - 1].id === pendingId) {
      messages.splice(messages.length - 2, 2);
    }
    return {
      ...s, status: 'idle', turn: null, messages, error: action.error || { message: 'Could not send the message.' },
    };
  }

  case 'step': {
    const text = stepText(action);
    if (!text) return s;
    return patchPending(s, (m) => (m.steps[m.steps.length - 1] === text
      ? m : { ...m, steps: [...m.steps, text] }));
  }

  case 'message': {
    const text = asText(action.text);
    if (!text) return s;
    return patchPending(s, (m) => ({ ...m, text: m.text ? `${m.text}\n\n${text}` : text }));
  }

  case 'revision':
    return patchPending(s, {
      jobIdAfter: action.job_id || null,
      diff: asDiff(action.diff_summary),
      changed: true,
    });

  case 'done': {
    if (!s.turn) return s;
    const charged = typeof action.credits_charged === 'number' ? action.credits_charged : 0;
    const next = patchPending(s, (m) => ({
      ...m,
      status: 'done',
      creditsCharged: charged,
      changed: action.changed === undefined ? m.changed : !!action.changed,
      text: m.text || (action.changed ? 'Done.' : 'Nothing was changed.'),
    }));
    return {
      ...next, status: 'idle', turn: null, allowance: mergeAllowance(s.allowance, action),
    };
  }

  case 'error': {
    if (!s.turn) return s;
    const next = patchPending(s, (m) => ({
      ...m,
      status: 'error',
      text: asText(action.message) || 'Something went wrong. Nothing was charged.',
    }));
    return { ...next, status: 'idle', turn: null };
  }

  case 'snapshot': {
    // Poll fallback: the accumulated turn replaces what the stream built.
    const snap = snapshotFromEvents(action.snapshot || {});
    let next = patchPending(s, (m) => {
      const steps = Array.isArray(snap.steps) ? snap.steps.map(stepText).filter(Boolean) : m.steps;
      let { text } = m;
      if (Array.isArray(snap.messages)) text = snap.messages.map(stepText).filter(Boolean).join('\n\n');
      else if (typeof snap.message === 'string') text = snap.message;
      else if (snap.message && typeof snap.message.text === 'string') text = snap.message.text;
      return { ...m, steps, text };
    });
    if (snap.revision) next = chatReducer(next, { type: 'revision', ...snap.revision });
    const st = snap.status;
    if (st === 'done' || st === 'completed' || snap.done) {
      next = chatReducer(next, { type: 'done', ...(snap.done || snap) });
    } else if (st === 'error' || st === 'failed' || snap.error) {
      const err = snap.error && typeof snap.error === 'object' ? snap.error : { message: snap.error };
      next = chatReducer(next, { type: 'error', ...err });
    }
    return next;
  }

  case 'clearError':
    return { ...s, error: null };

  default:
    return s;
  }
}

/** Is the accumulated poll snapshot terminal? */
export const isTurnSnapshotFinal = (snap) => !!snap && (
  ['done', 'completed', 'error', 'failed'].includes(snap.status) || !!snap.done || !!snap.error
);

/**
 * Incremental SSE parser. Feed it the buffer (previous rest + new chunk);
 * returns complete events and the unparsed tail. An event ends at a blank
 * line; several `data:` lines are joined with "\n"; malformed JSON is skipped.
 *
 * @param {string} buffer
 * @returns {{ events: Array<{event: string, data: any}>, rest: string }}
 */
export function parseSseBuffer(buffer) {
  const text = String(buffer || '').replace(/\r\n/g, '\n');
  const blocks = text.split('\n\n');
  const rest = blocks.pop();
  const events = [];
  blocks.forEach((block) => {
    let event = 'message';
    const data = [];
    block.split('\n').forEach((line) => {
      if (line.startsWith(':')) return; // comment / keep-alive
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
    });
    if (data.length === 0) return;
    try {
      events.push({ event, data: JSON.parse(data.join('\n')) });
    } catch { /* skip malformed JSON */ }
  });
  return { events, rest };
}

/**
 * The price line next to Send: "Included · 7 of 10 edits left" or "Next
 * change costs 1 credit — opens 10 more edits". Without a server allowance
 * it is estimated from GET /quota → pricing and the widget's origin.
 *
 * @param {object|null} allowance  allowanceFrom(...) / state.allowance
 * @param {{ edit_pack_size?: number, edit_pack_price?: number,
 *           edit_included_generated?: number }} [pricing]
 * @param {{ imported?: boolean }} [opts]
 * @returns {{ cost: number, label: string }} cost = credits the next change takes
 */
export function editAllowanceLabel(allowance, pricing = {}, opts = {}) {
  const packSize = pricing.edit_pack_size > 0 ? pricing.edit_pack_size : 10;
  const packPrice = typeof pricing.edit_pack_price === 'number' ? pricing.edit_pack_price : 1;
  const includedGen = pricing.edit_included_generated > 0
    ? pricing.edit_included_generated : packSize;
  const a = allowance || {};
  const total = a.included > 0 ? a.included : packSize;

  if (a.nextCost > 0) {
    return { cost: a.nextCost, label: `Next change costs ${credits(a.nextCost)} — opens ${packSize} more edits` };
  }
  if (a.nextCost === 0 || a.editsLeft > 0) {
    if (typeof a.editsLeft === 'number') {
      return { cost: 0, label: `Included · ${Math.min(a.editsLeft, total)} of ${total} edits left` };
    }
    return { cost: 0, label: 'Included in your edit pack' };
  }
  // Nothing reported yet: estimate.
  if (opts.imported) {
    if (packPrice > 0) {
      return { cost: packPrice, label: `First change costs ${credits(packPrice)} — opens ${packSize} edits` };
    }
    return { cost: 0, label: `${packSize} edits included` };
  }
  return { cost: 0, label: `Included · ${includedGen} edits with every generated widget` };
}

export const QUESTIONS_FREE_NOTE = 'Questions are always free';

/** The small tag under an assistant message: what that turn cost. */
export function chargedLabel(msg) {
  if (!msg || msg.role !== 'assistant' || msg.status !== 'done') return null;
  if (msg.creditsCharged > 0) return `${msg.creditsCharged} ${msg.creditsCharged === 1 ? 'credit' : 'credits'}`;
  if (msg.creditsCharged === 0) return msg.changed ? 'Free' : 'Free · nothing changed';
  return null;
}

/**
 * Readable text for a failed POST /edit-chat (`price` = what the next change costs).
 * @returns {{ message: string, buy: boolean, fallback: boolean }}
 */
export function chatSendError(err, price = 1) {
  const status = err?.statusCode;
  const code = err?.error || err?.code;
  if (status === 404) {
    return { message: 'Chat editing is not available right now.', buy: false, fallback: true };
  }
  if (status === 402 || code === 'INSUFFICIENT_CREDITS') {
    return {
      message: `Not enough credits. This change costs ${credits(price)} and opens a new pack of edits.`,
      buy: true,
      fallback: false,
    };
  }
  if (status === 403 || code === 'PURCHASE_REQUIRED') {
    return {
      message: err?.message || 'Editing with AI on this account is unlocked by a credit purchase.',
      buy: code === 'PURCHASE_REQUIRED' || status === 403,
      fallback: false,
    };
  }
  if (status === 409) {
    return { message: 'An edit is already running. Wait for it to finish.', buy: false, fallback: false };
  }
  if (status === 422 || code === 'BASE_TOO_LARGE') {
    return {
      message: err?.message || 'This widget is too large to edit in the chat.',
      buy: false,
      fallback: false,
    };
  }
  if (status === 401) return { message: 'Please sign in to edit the widget.', buy: false, fallback: false };
  if (status === 429) {
    return { message: err?.message || 'Please wait a moment before sending again.', buy: false, fallback: false };
  }
  return { message: err?.message || 'Could not send the message. Please try again.', buy: false, fallback: false };
}

const COLOR_TYPES = /colou?r/i;
const ALARM_NAME = /alarm|fault|error|trip|warn/i;
const TEXT_TYPES = /string|text/i;

/**
 * Quick suggestion chips for the widget on screen, from its properties
 * (render_metadata.params: [{ name, type }]). At most four, always ending
 * with the free question.
 *
 * @param {Array<{name: string, type?: string}>} params
 * @returns {string[]}
 */
export function suggestionChips(params) {
  const list = (Array.isArray(params) ? params : []).filter((p) => p && p.name);
  const names = list.map((p) => String(p.name));
  const hasColor = list.some((p) => COLOR_TYPES.test(p.type || '') || COLOR_TYPES.test(p.name));
  const hasOutlineColor = names.some((n) => /outline|stroke|border/i.test(n));
  const alarm = list.find((p) => ALARM_NAME.test(p.name));
  const hasText = list.some((p) => TEXT_TYPES.test(p.type || '') || /text|label|caption/i.test(p.name));
  const numeric = list.find((p) => /number|int|real|float|double/i.test(p.type || '')
    || /^(value|level|speed|position|setpoint)$/i.test(p.name));

  const chips = [];
  if (!hasOutlineColor) chips.push('Add a colour property for the outline');
  if (alarm) chips.push(`Make it blink while ${alarm.name} is on`);
  else chips.push('Make it blink on alarm');
  if (!hasText) chips.push('Bind the text to a property');
  else if (numeric) chips.push(`Show ${numeric.name} as a number`);
  if (!hasColor && chips.length < MAX_CHIPS - 1) chips.push('Add a colour property for the fill');
  const out = chips.slice(0, MAX_CHIPS - 1);
  out.push(list.length > 0 ? 'What properties does it have?' : 'What can I bind in this widget?');
  return out;
}

/** "usually about 25 s" from estimate_seconds, or null. */
export function usualWaitLabel(estimate) {
  const typical = estimate && Number(estimate.typical);
  if (!typical || !Number.isFinite(typical) || typical <= 0) return null;
  if (typical < 90) return `usually about ${Math.round(typical)} s`;
  return `usually about ${Math.round(typical / 60)} min`;
}
